import { DateTime } from 'luxon';
import { Instance, flow, types } from 'mobx-state-tree';

import { getAvailableReworkingSlots } from '@common/api/meetSlots';
import { TIME_ZONE } from '@common/constants/academyReworkings';
import { groupSlots } from '@common/helpers/academyReworkings';
import { AvailableSlot, TimeWindow } from '@common/types/academyReworkings';

import fetchAll from './helpers/fetchAll';

const StudentSlots = types
	.model('AcademyStudentSlots', {
		groupId: types.number,
		programId: types.number,
		locationId: types.number,
		windowsByWeek: types.optional(types.frozen<Record<string, TimeWindow[]>>(), {}),
	})
	.volatile(() => ({ loading: false }))
	.actions((self) => {
		let revision = 0;
		return {
			clear() {
				revision += 1;
				self.windowsByWeek = {};
				self.loading = false;
			},
			fetch: flow(function* (weeks: string[]) {
				revision += 1;
				const request = revision;
				self.loading = true;
				try {
					const results: { start: string; slots: AvailableSlot[]; windows: TimeWindow[] }[] =
						yield Promise.all(
							weeks.map(async (start) => {
								const week = DateTime.fromISO(start, { zone: TIME_ZONE });
								const slots = await fetchAll<AvailableSlot>(
									(page, limit) =>
										getAvailableReworkingSlots({
											service_id: 1,
											type: 'working_off',
											status: 0,
											group_id: self.groupId,
											program_id: self.programId,
											location_id: self.locationId,
											period_start: week.startOf('week').toISODate(),
											period_end: week.endOf('week').toISODate(),
											page,
											limit,
										}),
									300,
								);
								return { start, slots, windows: groupSlots(slots) };
							}),
						);
					if (request === revision) {
						self.windowsByWeek = {
							...self.windowsByWeek,
							...Object.fromEntries(results.map((result) => [result.start, result.windows])),
						};
					}
					return results;
				} finally {
					if (request === revision) self.loading = false;
				}
			}),
		};
	});

export default StudentSlots;
export interface IStudentSlotsModel extends Instance<typeof StudentSlots> {}
